import { useMemo } from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAtom } from 'jotai';
import * as analysisState from '@/components/Analysis/state';
import {
  useRemediatedAssemblyPaintRegionVersionsByPaintRegionQuery,
  useRemediatedPoiVersionsByPaintRegionQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { HistoryContainer, HistoryList } from './styles';
import { HistoryEntryItem, HistoryEntryData } from './HistoryEntryItem';

export const PaintBlockHistory = () => {
  const [selectedPaintBlockHistory, setSelectedPaintBlockHistory] = useAtom(
    analysisState.selectedPaintBlockHistory
  );
  const structureId = useGetStructureId();

  const {
    data: assemblyData,
    loading: assemblyLoading,
    error: assemblyError,
  } = useRemediatedAssemblyPaintRegionVersionsByPaintRegionQuery({
    variables: {
      input: {
        structureId,
        paintRegionId: selectedPaintBlockHistory?.id.toString() || '',
      },
    },
    skip: !selectedPaintBlockHistory?.id,
  });

  const {
    data: poiData,
    loading: poiLoading,
    error: poiError,
  } = useRemediatedPoiVersionsByPaintRegionQuery({
    variables: {
      input: {
        structureId,
        paintRegionId: selectedPaintBlockHistory?.id.toString() || '',
      },
    },
    skip: !selectedPaintBlockHistory?.id,
  });

  const groupedEntries = useMemo(() => {
    const grouped = new Map<string, HistoryEntryData>();

    // Helper function to round timestamp to nearest minute
    const roundToMinute = (timestamp: string) => {
      let date: Date;

      const trimmedTimestamp = timestamp.trim();
      const numericValue = Number.parseInt(trimmedTimestamp, 10);

      if (!Number.isNaN(numericValue) && numericValue.toString() === trimmedTimestamp) {
        date = new Date(numericValue);
      } else {
        date = new Date(timestamp);
      }

      date.setSeconds(0, 0); // Reset seconds and milliseconds
      return date.getTime().toString();
    };

    // Process POI data
    poiData?.remediatedPOIVersionsByPaintRegion?.forEach((version) => {
      const roundedTime = roundToMinute(version.createdAt);
      const userId = version.lastModifiedByUser?.id || 'unknown';
      const roundedKey = `${roundedTime}_${userId}`;

      // Extract remediationAt from updatedData if available
      let remediationAtValue: string | undefined = undefined;
      if (version.updatedData) {
        try {
          const updatedData =
            typeof version.updatedData === 'string'
              ? JSON.parse(version.updatedData)
              : version.updatedData;
          if (updatedData.remediationAt) {
            remediationAtValue = updatedData.remediationAt;
          }
        } catch (e) {
          // If parsing fails, ignore
        }
      }

      if (!grouped.has(roundedKey)) {
        const userName = version.lastModifiedByUser?.name || 'Unknown User';
        grouped.set(roundedKey, {
          createdAt: roundedTime,
          userName,
          pois: [],
          assemblies: [],
          remediationAt: remediationAtValue,
        });
      } else if (remediationAtValue && !grouped.get(roundedKey)!.remediationAt) {
        // If this version has remediationAt and the group doesn't, add it
        grouped.get(roundedKey)!.remediationAt = remediationAtValue;
      }

      grouped.get(roundedKey)!.pois.push({
        id: version.id,
        name: version.name || undefined,
        poiId: version.poiId || undefined,
      });
    });

    // Process Assembly data
    assemblyData?.remediatedAssemblyPaintRegionVersionsByPaintRegion?.forEach((version) => {
      const roundedTime = roundToMinute(version.createdAt);
      const userId = version.lastModifiedByUser?.id || 'unknown';
      const roundedKey = `${roundedTime}_${userId}`;

      // Check if updatedData contains remediationChange
      const displayName = version.name || 'Unknown Component';
      let areaValue: number | undefined = undefined;
      if (version.updatedData.remediationChange) {
        try {
          const updatedData =
            typeof version.updatedData === 'string'
              ? JSON.parse(version.updatedData)
              : version.updatedData;

          if (updatedData.remediationChange !== undefined) {
            areaValue = updatedData.remediationChange;
          }
        } catch (e) {
          // If parsing fails, ignore
        }
      }

      const lastModifiedFor = version.lastModifiedFor || undefined;

      // Extract remediationAt from updatedData if available
      let remediationAtValue: string | undefined = undefined;
      if (version.updatedData) {
        try {
          const updatedData =
            typeof version.updatedData === 'string'
              ? JSON.parse(version.updatedData)
              : version.updatedData;
          if (updatedData.remediationAt) {
            remediationAtValue = updatedData.remediationAt;
          }
        } catch (e) {
          // If parsing fails, ignore
        }
      }

      if (!grouped.has(roundedKey)) {
        const userName = version.lastModifiedByUser?.name || 'Unknown User';
        grouped.set(roundedKey, {
          createdAt: roundedTime,
          userName,
          pois: [],
          assemblies: [],
          area: areaValue,
          remediationAt: remediationAtValue,
          lastModifiedFor,
        });
      } else {
        if (remediationAtValue && !grouped.get(roundedKey)!.remediationAt) {
          // If this version has remediationAt and the group doesn't, add it
          grouped.get(roundedKey)!.remediationAt = remediationAtValue;
        }
        if (lastModifiedFor && !grouped.get(roundedKey)!.lastModifiedFor) {
          // If this version has lastModifiedFor and the group doesn't, add it
          grouped.get(roundedKey)!.lastModifiedFor = lastModifiedFor;
        }
      }

      grouped.get(roundedKey)!.assemblies.push({
        id: version.id,
        name: displayName,
        area: areaValue,
      });
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const timestampA = Number.parseInt(a.createdAt, 10);
      const timestampB = Number.parseInt(b.createdAt, 10);
      return timestampB - timestampA; // Latest first (descending order)
    });
  }, [poiData, assemblyData]);

  const handleClose = () => {
    setSelectedPaintBlockHistory(undefined);
  };

  if (!selectedPaintBlockHistory) {
    return null;
  }

  const loading = poiLoading || assemblyLoading;
  const error = poiError || assemblyError;

  return (
    <HistoryContainer>
      {/* Header with close button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">History: {selectedPaintBlockHistory.block}</Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'text.primary' }}>
          <Close />
        </IconButton>
      </Box>

      {/* Content */}
      <HistoryList>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Typography color="error">Error loading history: {error.message}</Typography>}

        {!loading && !error && groupedEntries.length === 0 && (
          <Typography variant="body1">No history available</Typography>
        )}

        {!loading && !error && groupedEntries.length > 0 && (
          <>
            {groupedEntries.map((entry, index) => (
              <HistoryEntryItem
                key={entry.createdAt}
                entry={entry}
                isFirst={index === 0}
                isLast={index === groupedEntries.length - 1}
              />
            ))}
          </>
        )}
      </HistoryList>
    </HistoryContainer>
  );
};
